// =====================================================
// OPTION 1: MARKDOWN MESSAGE RENDERER
// Add react-markdown to render formatted AI messages
// =====================================================

// STEP 1: Install react-markdown
// Run: npm install react-markdown

// STEP 2: Create this component
// Save as: frontend/src/components/MessageContent.js

import React from 'react';
import ReactMarkdown from 'react-markdown';
import './MessageContent.css';

/**
 * Smart message content renderer
 * - Renders plain text for user messages
 * - Renders markdown for AI messages (recommendations, etc.)
 */
const MessageContent = ({ content, role, isRecommendation = false }) => {
  // Check if content contains markdown formatting
  const hasMarkdown = content && (
    content.includes('**') ||
    content.includes('##') ||
    content.includes('• ') ||
    content.includes('---') ||
    content.includes('📊') ||
    content.includes('🎯') ||
    content.includes('📋')
  );
  
  // User messages: plain text
  if (role === 'user') {
    return <span className="message-text-plain">{content}</span>;
  }
  
  // AI messages with markdown: render formatted
  if (hasMarkdown || isRecommendation) {
    return (
      <div className="message-text-formatted">
        <ReactMarkdown
          components={{
            // Custom heading styles
            h1: ({ node, ...props }) => <h3 className="msg-heading-1" {...props} />,
            h2: ({ node, ...props }) => <h4 className="msg-heading-2" {...props} />,
            h3: ({ node, ...props }) => <h5 className="msg-heading-3" {...props} />,
            
            // Custom paragraph
            p: ({ node, ...props }) => <p className="msg-paragraph" {...props} />,
            
            // Custom list styling
            ul: ({ node, ...props }) => <ul className="msg-list" {...props} />,
            ol: ({ node, ...props }) => <ol className="msg-list-ordered" {...props} />,
            li: ({ node, ...props }) => <li className="msg-list-item" {...props} />,
            
            // Bold text
            strong: ({ node, ...props }) => <strong className="msg-bold" {...props} />,
            
            // Horizontal rule (section divider)
            hr: ({ node, ...props }) => <hr className="msg-divider" {...props} />,
            
            // Code blocks
            code: ({ node, inline, ...props }) => 
              inline 
                ? <code className="msg-code-inline" {...props} />
                : <code className="msg-code-block" {...props} />
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  }
  
  // Plain AI messages
  return <span className="message-text-plain">{content}</span>;
};

export default MessageContent;